import { store, workosAuthAtom, workosAuthInitialState } from "~/atoms";

import { redirectToAuthkitLogin } from "./authProvider";
import { AuthkitError, getSession, postRefresh } from "./authkitClient";
import type { AuthkitRefreshResult } from "./authkitClient";

/**
 * Silent-refresh coordinator (V3 — INS-1394). Owns the single-flight
 * `ensureFreshSession` that all refresh triggers funnel through (a 401 in
 * the fetch wrapper today; visibilitychange + an expiry timer in V3 task 3).
 * Runs OUTSIDE the React tree, so it reads/writes WorkOS auth state through
 * the shared jotai `store` (the one App.tsx mounts via JotaiProvider) rather
 * than hooks.
 *
 * Design: ADR-001 "Refresh Flow Design".
 */

/**
 * Skew buffer for `known`-confidence triggers (401, visibilitychange, expiry
 * timer): refresh when within SKEW_MS of expiry instead of waiting for the
 * token to actually lapse. `suspected` triggers (V4 `<img onError>`) use a
 * strict expiry check (buffer 0) so an ambiguous 404 can't trigger a refresh
 * cascade close to — but before — expiry.
 */
export const SKEW_MS = 30_000;

type Confidence = "known" | "suspected";

interface EnsureFreshSessionOptions {
  confidence: Confidence;
  /**
   * Reactive refreshes (recovering from an observed failure, e.g. a 401)
   * bump `authGen` so V4's `<img src=?v=authGen>` remounts and re-requests
   * with the fresh cookie. Proactive refreshes (timer/visibility) leave
   * `authGen` untouched so native image caching keeps working.
   */
  reactive?: boolean;
}

/** Module-scope single-flight promise (S_inflight). */
let refreshInFlight: Promise<void> | null = null;

/**
 * Coalesced session refresh. Returns early when the access token is still
 * fresh enough for the trigger's confidence; otherwise it joins (or starts)
 * the one in-flight POST /api/refresh. Forty simultaneous 401s on tab
 * foreground produce exactly one refresh attempt.
 */
export function ensureFreshSession(
  options: EnsureFreshSessionOptions,
): Promise<void> {
  const { confidence, reactive = false } = options;
  const { expiresAt } = store.get(workosAuthAtom);
  const buffer = confidence === "known" ? SKEW_MS : 0;

  if (expiresAt != null) {
    // Token still comfortably fresh for this trigger — nothing to do.
    if (Date.now() < expiresAt - buffer) return Promise.resolve();
  } else if (confidence === "suspected") {
    // Expiry unknown (not yet hydrated). A `suspected` trigger (ambiguous,
    // e.g. an image 404) can't justify a blind refresh; a `known` trigger
    // (the server actually rejected us) falls through and refreshes.
    return Promise.resolve();
  }

  if (!refreshInFlight) {
    refreshInFlight = doRefresh(reactive).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(reactive: boolean): Promise<void> {
  const { csrfToken } = store.get(workosAuthAtom);
  if (!csrfToken) {
    // No session-bound CSRF token means no session to refresh.
    redirectToLogin();
    return;
  }

  try {
    applyRefreshResult(await postRefresh(csrfToken), reactive);
  } catch (err) {
    if (err instanceof AuthkitError && err.status === 401) {
      // Refresh token dead/rotated/revoked — clear in-memory auth state and
      // bounce to authkit-ui login (same redirect WorkosPrivateRoutes uses).
      dropToLogin();
      return;
    }
    if (err instanceof AuthkitError && err.status === 403) {
      // Stale CSRF token (they expire 15 min after issue). The session
      // itself may still be alive — re-hydrate via GET /api/session, which
      // mints a fresh session-bound token, then retry the refresh once.
      await recoverFromStaleCsrf(reactive);
      return;
    }
    // Network error or 5xx: leave state intact and re-throw so the caller
    // (e.g. the 401 retry in createAxiosRequest) can fall back to surfacing
    // the original error rather than masking a transient outage as logout.
    throw err;
  }
}

/**
 * A 403 from /api/refresh means our CSRF token aged out. Re-fetch the
 * session (mints a fresh token bound to the same `sid`) and retry once.
 * If the session is gone, or the fresh token is still rejected, drop to
 * login; network/5xx failures re-throw like the primary path.
 */
async function recoverFromStaleCsrf(reactive: boolean): Promise<void> {
  const session = await getSession();
  if (!session) {
    // meq_at cookie gone/expired — nothing left to refresh.
    dropToLogin();
    return;
  }

  // Store the re-minted token immediately so other callers (e.g. logout)
  // use it even if the retry below fails transiently.
  store.set(workosAuthAtom, (prev) => ({
    ...prev,
    csrfToken: session.csrfToken,
  }));

  try {
    applyRefreshResult(await postRefresh(session.csrfToken), reactive);
  } catch (err) {
    if (
      err instanceof AuthkitError &&
      (err.status === 401 || err.status === 403)
    ) {
      // A freshly minted token was still rejected — the session is not
      // recoverable from here.
      dropToLogin();
      return;
    }
    throw err;
  }
}

function applyRefreshResult(
  result: AuthkitRefreshResult,
  reactive: boolean,
): void {
  store.set(workosAuthAtom, (prev) => ({
    ...prev,
    expiresAt: result.expiresAt,
    // The worker rotates the CSRF token on every successful refresh; keep
    // the current one only if this response predates that (older build).
    csrfToken: result.csrfToken ?? prev.csrfToken,
    authGen: reactive ? prev.authGen + 1 : prev.authGen,
  }));
}

function dropToLogin(): void {
  store.set(workosAuthAtom, workosAuthInitialState);
  redirectToLogin();
}

function redirectToLogin(): void {
  redirectToAuthkitLogin({
    returnTo: window.location.pathname + window.location.search,
  });
}

/**
 * Test-only: reset the module-scope single-flight promise between cases so a
 * pending refresh from one test can't leak into the next.
 */
export function __resetRefreshCoordinatorForTests(): void {
  refreshInFlight = null;
}
